import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const StudentTeachers = ({ onBack }) => {
  const { user } = useAuth()
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user && user.uid) {
      loadTeachers()
    }
  }, [user])

  const loadTeachers = async () => {
    try {
      setLoading(true)
      console.log('🔍 Öğretmenler yükleniyor...')
      
      // Önce öğrencinin katıldığı sınıfları al
      const classesRef = collection(db, 'classes')
      const classesQuery = query(
        classesRef,
        where('studentIds', 'array-contains', user.uid)
      )
      
      const classesSnapshot = await getDocs(classesQuery)
      console.log('📚 Öğrencinin katıldığı sınıf sayısı:', classesSnapshot.size)
      
      const teacherIds = []
      
      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data()
        const teacherId = classData.teacherId
        if (teacherId && !teacherIds.includes(teacherId)) {
          teacherIds.push(teacherId)
          console.log('👨‍🏫 Öğretmen ID eklendi:', teacherId)
        }
      }
      
      if (teacherIds.length === 0) {
        console.log('❌ Öğrenci için öğretmen bulunamadı')
        setTeachers([])
        return
      }
      
      // Şimdi öğretmen detaylarını ve müsaitlik durumlarını al
      await loadTeacherDetails(teacherIds)
      
    } catch (error) {
      console.error('Error loading teachers:', error)
      toast.error('Öğretmenler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const loadTeacherDetails = async (teacherIds) => {
    const teachersData = []
    
    for (const teacherId of teacherIds) {
      try {
        console.log('👨‍🏫 Öğretmen detayları yükleniyor:', teacherId)
        
        // Öğretmen bilgilerini al
        const teacherDoc = await getDoc(doc(db, 'users', teacherId))
        
        if (teacherDoc.exists()) {
          const teacherData = teacherDoc.data()
          const name = teacherData.username || teacherData.email || 'Bilinmeyen Öğretmen'
          
          console.log('📝 Öğretmen adı:', name)
          
          // Müsaitlik durumunu kontrol et
          const availabilityDoc = await getDoc(doc(db, 'teacherAvailability', teacherId))
          const isAvailable = availabilityDoc.exists() && availabilityDoc.data().isAvailable === true
          
          console.log('✅ Öğretmen müsaitlik durumu:', isAvailable)
          
          teachersData.push({
            id: teacherId,
            name,
            isAvailable,
            profileImageUrl: teacherData.profileImageUrl
          })
        } else {
          console.log('❌ Öğretmen dokümanı bulunamadı:', teacherId)
        }
      } catch (error) {
        console.error('Error loading teacher details:', teacherId, error)
      }
    }
    
    console.log('📋 Yüklenen öğretmenler:', teachersData)
    setTeachers(teachersData)
  }

  const handleTeacherClick = (teacher) => {
    if (teacher.isAvailable) {
      // Soru gönderme sayfasına yönlendir
      onBack({ 
        page: 'send-question', 
        teacherData: teacher 
      })
    } else {
      toast.error('Bu öğretmen şu anda müsait değil')
    }
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Öğretmenlerim
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {!user || !user.uid ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Kullanıcı bilgileri yükleniyor...</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Öğretmenler yükleniyor...</Typography>
          </Box>
        ) : teachers.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 60, color: '#6E6D6D', mb: 2 }} />
              <Typography variant="h6" color="#6E6D6D" gutterBottom>
                Henüz öğretmeniniz yok
              </Typography>
              <Typography variant="body2" color="#6E6D6D">
                Sınıflara katıldığınızda öğretmenleriniz burada görünecek
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List>
            {teachers.map((teacher, index) => (
              <React.Fragment key={teacher.id}>
                                 <ListItem 
                   button={teacher.isAvailable}
                   onClick={() => handleTeacherClick(teacher)}
                   sx={{ 
                     display: 'flex', 
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     py: 2,
                     mb: 2,
                     borderRadius: 2,
                     border: '2px solid',
                     borderColor: teacher.isAvailable ? 'primary.main' : 'grey.300',
                     backgroundColor: teacher.isAvailable ? 'background.paper' : 'grey.50',
                     cursor: teacher.isAvailable ? 'pointer' : 'default',
                     opacity: teacher.isAvailable ? 1 : 0.6,
                     boxShadow: teacher.isAvailable ? 2 : 1,
                     '&:hover': teacher.isAvailable ? {
                       backgroundColor: 'primary.50',
                       borderColor: 'primary.dark',
                       transform: 'translateY(-2px)',
                       boxShadow: 4
                     } : {},
                     transition: 'all 0.2s ease-in-out'
                   }}
                 >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <ListItemAvatar>
                      <Avatar>
                        {teacher.profileImageUrl ? (
                          <img 
                            src={teacher.profileImageUrl} 
                            alt={teacher.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <PersonIcon />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={teacher.name}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip
                            icon={teacher.isAvailable ? <CheckCircleIcon /> : <CancelIcon />}
                            label={teacher.isAvailable ? 'Müsait' : 'Müsait Değil'}
                            size="small"
                            color={teacher.isAvailable ? 'success' : 'error'}
                            variant="filled"
                          />
                        </Box>
                      }
                    />
                  </Box>
                                 </ListItem>
               </React.Fragment>
            ))}
          </List>
        )}
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default StudentTeachers
